import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";

/**
 * Reports route guards that render nothing the user can see.
 *
 * Sentry cannot find this class of bug on its own: a guard that never resolves,
 * or content parked at opacity 0 by an abandoned animation, throws no error and
 * logs no warning. It is a perfectly healthy React tree that happens to be
 * invisible. The only signal is that some seconds after mount there is still
 * nothing on screen — so we check for exactly that.
 *
 * Two distinct failures are caught:
 *   - `guard-never-resolved` — the gate is still loading long past any plausible
 *     request, so the user is staring at a spinner (or at nothing).
 *   - `content-invisible` — the gate resolved and the content mounted, but it is
 *     transparent, collapsed, or detached. This is the shape of the blank-page
 *     bug that motivated the watchdog.
 */
const WATCHDOG_MS = 8000;

/** One report per surface per page load; a stuck guard must not become a firehose. */
const reported = new Set<string>();

type StuckReason = "guard-never-resolved" | "content-never-mounted" | "content-invisible";

function diagnose(ready: boolean, el: HTMLElement | null): StuckReason | null {
  if (!ready) return "guard-never-resolved";
  if (!el) return "content-never-mounted";
  // `offsetParent` is null for a detached element or one inside `display: none`.
  // Position-fixed subtrees also report null, which is why the opacity and box
  // checks below stand on their own rather than gating on it.
  const style = window.getComputedStyle(el);
  if (parseFloat(style.opacity) === 0) return "content-invisible";
  if (style.visibility === "hidden" || style.display === "none") return "content-invisible";
  if (el.getClientRects().length === 0) return "content-invisible";
  return null;
}

/**
 * Watches one guarded surface. Attach the returned ref to the element that holds
 * the guard's content.
 *
 * @param surface Stable name for the guard, used to group reports in Sentry.
 * @param ready   The guard's own resolved flag.
 */
export function useBlankScreenWatchdog<T extends HTMLElement = HTMLDivElement>(
  surface: string,
  ready: boolean
) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    if (reported.has(surface)) return;

    const timer = setTimeout(() => {
      const reason = diagnose(ready, ref.current);
      if (!reason || reported.has(surface)) return;
      reported.add(surface);

      Sentry.captureMessage(`Blank surface: ${surface} (${reason})`, {
        level: "error",
        tags: { blank_screen: "true", surface, reason },
        extra: {
          pathname: window.location.pathname,
          ready,
          documentHidden: document.hidden,
          msSinceNavigation: Math.round(performance.now()),
          opacity: ref.current
            ? window.getComputedStyle(ref.current).opacity
            : null,
        },
      });
    }, WATCHDOG_MS);

    return () => clearTimeout(timer);
  }, [surface, ready]);

  return ref;
}
